import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { LogIn } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_volunteer-dispatch-6/artifacts/8p87dbov_hope-to-home-350x66.png";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const res = await login(email, password);
    setSubmitting(false);
    if (res.ok) {
      toast.success("Welcome back!");
      navigate(res.user?.role === "admin" ? "/admin" : "/");
    } else {
      toast.error(res.error || "Login failed");
    }
  };

  return (
    <div className="min-h-screen surface-public grain flex flex-col items-center justify-center px-4">
      <Link to="/" className="mb-8 focus-ring rounded-md" data-testid="back-to-public-link">
        <img src={LOGO_URL} alt="Hope to Home" className="h-12 w-auto" />
      </Link>
      <div className="w-full max-w-sm card-elevated rounded-xl p-7 sm:p-8">
        <h1 className="font-heading text-3xl font-bold text-slate-900">Sign in</h1>
        <p className="text-sm text-slate-500 mt-1.5">Members & organizers — pick a Saturday slot or run the dashboard.</p>
        <form onSubmit={submit} className="space-y-4 mt-6">
          <div>
            <Label htmlFor="login-email" className="text-sm font-semibold">Email</Label>
            <Input
              id="login-email"
              data-testid="login-email-input"
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="mt-1.5 text-base min-h-[44px]"
            />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label htmlFor="login-password" className="text-sm font-semibold">Password</Label>
              <Link
                to="/forgot-password"
                data-testid="login-forgot-password-link"
                className="text-xs font-semibold text-brand-blue hover:underline"
              >
                Forgot password?
              </Link>
            </div>
            <Input
              id="login-password"
              data-testid="login-password-input"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="mt-1.5 text-base min-h-[44px]"
            />
          </div>
          <Button
            type="submit"
            data-testid="login-submit-button"
            disabled={submitting}
            className="w-full min-h-[48px] bg-brand-blue hover:bg-brand-blue-dark text-white font-semibold"
          >
            <LogIn className="w-4 h-4 mr-2" />
            {submitting ? "Signing in…" : "Sign in"}
          </Button>
        </form>
        <p className="text-xs text-slate-500 mt-6 text-center leading-relaxed">
          Don't have an account yet?{" "}
          <Link to="/register" className="text-brand-blue font-semibold hover:underline" data-testid="login-to-register-link">
            Create one with your invite code
          </Link>
        </p>
      </div>
    </div>
  );
}
